"use client"
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuthHeader } from '../utils/auth';
import { useOutletContext } from 'react-router-dom';
import { isEnglishOperatorMode } from '../utils/operatorMode';
import Papa from 'papaparse';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

// --- SVG Icons ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
// ★追加: 一括削除用のゴミ箱アイコン
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;

// --- Helper Components ---

function EditableCell({ value, onSave, emptyText = 'ー', disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value || "");

  useEffect(() => {
    setCurrentValue(value || "");
  }, [value]);

  const handleSave = () => {
    setEditing(false);
    if (currentValue !== value) {
      onSave(currentValue);
    }
  };

  return (
    <td className="py-2 px-3 border-b border-gray-200" onDoubleClick={() => { if (!disabled) setEditing(true); }}>
      {editing ? (
        <input
          type="text"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
          className="w-full p-1 border rounded-md text-sm"
          autoFocus
        />
      ) : (
        <div className={`truncate text-sm ${disabled ? 'text-gray-500' : ''}`} title={value}>{value || emptyText}</div>
      )}
    </td>
  );
}


const getClientSalesListCopy = (isEnglishBeta) => isEnglishBeta ? {
    title: "Custom Lists",
    operationsTitle: "List Operations",
    uploadTitle: "CSV import",
    uploadEncodingNote: "Upload a UTF-8 CSV after completing the import details below.",
    chooseFile: "Select a CSV file.",
    uploading: "Processing...",
    execute: "Import",
    uploadStarting: "Uploading...",
    uploadAccepted: (jobId) => `Accepted (Job ID: ${jobId}). Import processing has started in the background.`,
    otherTitle: "Other actions",
    addManual: "Add manually",
    downloadAll: "Download all",
    downloadTemplate: "Template",
    bulkDelete: (count) => `Delete ${count} selected`,
    deleting: "Deleting...",
    searchPlaceholder: "Search company, URL, industry, location, or tags...",
    loading: "Loading...",
    empty: "No custom list records found.",
    errorPrefix: "Error: ",
    downloadNoData: "There is no data to download.",
    csvLibraryError: "CSV export library failed to load. Reload the page and try again.",
    downloadError: "Download error: ",
    templateDownloadError: "Template download failed.",
    companyPrompt: "Enter company name:",
    urlPrompt: "Enter company URL (required):",
    invalidUrl: "Enter a valid URL.",
    addSuccess: "New item added.",
    addError: "Add error: ",
    updateError: "Update error: ",
    deleteConfirm: "Delete this record?",
    deleteError: "Delete error: ",
    bulkDeleteConfirm: (count) => `Delete ${count} selected records?\nThis cannot be undone.`,
    bulkDeleteError: "Bulk delete error: ",
    urlRequired: "Company URL is required.",
    pageTotal: (count) => `${count.toLocaleString()} records`,
    noUrl: "No URL",
    dash: "-",
    columns: {
        company: "Company",
        url: "Company URL",
        contactUrl: "Contact URL",
        industry: "Industry",
        postalCode: "Postal Code",
        address: "Location",
        phone: "Phone",
        capital: "Capital",
        employees: "Employees",
        established: "Established",
        representative: "Representative",
        businessTags: "Business Tags",
        listTags: "List Tags",
        actions: "Actions",
        delete: "Delete",
    },
    pagination: {
        first: "First",
        previous: "Prev",
        next: "Next",
        last: "Last",
    },
    unlock: {
        title: "Custom Lists Unlock",
        unlockedTitle: "Custom Lists unlocked",
        lockedTitle: "Custom Lists locked",
        unlockedBody: "Your account can import and manage custom lists.",
        lockedBody: (count) => `Complete ${count} more setup item${count === 1 ? "" : "s"} to unlock custom list import.`,
        loading: "Checking unlock status...",
        clear: "Clear!",
        required: "Required",
        setupLink: "Open Sender & DNS settings",
    },
    attestation: {
        title: "Import Details",
        body: "Required for each custom list import or manual target addition.",
        targetCountry: "Target country",
        targetCountryPlaceholder: "United States",
        listSource: "List source",
        listSourcePlaceholder: "Select source",
        sourceDetail: "Source URL or description",
        sourceDetailPlaceholder: "Where did this list come from?",
        intendedUse: "Intended use",
        intendedUsePlaceholder: "Example: outreach to US SaaS companies for a Japan market-entry offer.",
        permission: "I have a lawful basis or permission to use this list for outreach.",
        responsibility: "I am responsible for sender identity, targeting, opt-out handling, and lawful outreach.",
        noSensitive: "This list does not contain sensitive, prohibited, or intentionally abusive targets.",
        unsubscribe: "I will honor unsubscribe, correction, deletion, and non-contact requests.",
        incomplete: "Complete the import details and acknowledgements first.",
        sourceOptions: [
            ["", "Select source"],
            ["owned_customer_or_prospect_list", "Owned customer/prospect list"],
            ["partner_or_event_list", "Partner or event list"],
            ["public_business_directory", "Public business directory"],
            ["manual_research", "Manual research"],
            ["other", "Other"],
        ],
    },
} : {
    title: "📂 マイリスト管理",
    operationsTitle: "リスト操作（UTF-8のエンコード形式でアップロード）",
    uploadTitle: "CSVアップロード",
    uploadEncodingNote: "",
    chooseFile: "ファイルを選択してください。",
    uploading: "処理中...",
    execute: "実行",
    uploadStarting: "アップロード中...",
    uploadAccepted: (jobId) => `受付完了 (Job ID: ${jobId})。バックグラウンドで処理が開始されます。リストの反映には時間がかかる場合があります。`,
    otherTitle: "その他",
    addManual: "手入力で追加",
    downloadAll: "全件ダウンロード",
    downloadTemplate: "雛形DL",
    bulkDelete: (count) => `${count}件を一括削除`,
    deleting: "削除中...",
    searchPlaceholder: "会社名、URL、業種、住所、タグで検索...",
    loading: "読み込み中...",
    empty: "表示できるリストはありません。",
    errorPrefix: "エラー: ",
    downloadNoData: "ダウンロードするデータがありません。",
    csvLibraryError: "CSV出力ライブラリの読み込みに失敗しました。ページをリロードするか、管理者に連絡してください。",
    downloadError: "ダウンロードエラー: ",
    templateDownloadError: "テンプレートのダウンロードに失敗しました。",
    companyPrompt: "追加する企業名を入力してください:",
    urlPrompt: "会社のURLを入力してください: (必須)",
    invalidUrl: "正しいURLを入力してください。",
    addSuccess: "新しい項目を追加しました。",
    addError: "追加エラー: ",
    updateError: "更新エラー: ",
    deleteConfirm: "この項目を本当に削除しますか？",
    deleteError: "削除エラー: ",
    bulkDeleteConfirm: (count) => `選択した ${count} 件のレコードを本当に一括削除しますか？\n※この操作は取り消せません。`,
    bulkDeleteError: "一括削除エラー: ",
    urlRequired: "会社URLは必須項目です。",
    pageTotal: (count) => `${count.toLocaleString()} 件中`,
    noUrl: "URLなし",
    dash: "ー",
    columns: {
        company: "会社名",
        url: "会社URL",
        contactUrl: "問合せURL",
        industry: "業種",
        postalCode: "郵便番号",
        address: "住所",
        phone: "電話番号",
        capital: "資本金",
        employees: "従業員数",
        established: "設立日",
        representative: "代表者名",
        businessTags: "事業タグ",
        listTags: "リストタグ",
        actions: "操作",
        delete: "削除",
    },
    pagination: {
        first: "最初",
        previous: "前へ",
        next: "次へ",
        last: "最後",
    },
    unlock: null,
    attestation: null,
};

function PaginationWithCopy({ currentPage, totalPages, onPageChange, copy }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.pagination.first}</button>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.pagination.previous}</button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.pagination.next}</button>
            <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{copy.pagination.last}</button>
        </div>
    );
}

// --- Main Page Component ---
function ClientSalesListPage({ trackingPath = '/app/marketing/tracking?surface=english_beta' }) {
    const { user, setActiveJob } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = useMemo(() => getClientSalesListCopy(isEnglishBeta), [isEnglishBeta]);
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [unlockStatus, setUnlockStatus] = useState(null);
    const [isUnlockLoading, setIsUnlockLoading] = useState(false);
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState("");
    const [importDetails, setImportDetails] = useState({
        target_country: "",
        list_source: "",
        source_url_or_description: "",
        intended_use: "",
        has_permission_or_legitimate_basis: false,
        sender_responsibility_acknowledged: false,
        no_sensitive_or_prohibited_targets_acknowledged: false,
        unsubscribe_handling_acknowledged: false,
    });

    // ★追加: 選択された行のIDを管理するステート
    const [selectedIds, setSelectedIds] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);

    const selectedClientId = useMemo(() => (user?.role === "admin" || user?.role === "admin_staff" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);
    const isEnglishLocked = Boolean(isEnglishBeta && unlockStatus?.applies && !unlockStatus?.unlocked);
    const mutationDisabled = Boolean(isEnglishBeta && (!unlockStatus || isUnlockLoading || isEnglishLocked));
    const isImportDetailsComplete = !isEnglishBeta || (
        importDetails.target_country.trim() &&
        importDetails.list_source.trim() &&
        importDetails.source_url_or_description.trim() &&
        importDetails.intended_use.trim() &&
        importDetails.has_permission_or_legitimate_basis &&
        importDetails.sender_responsibility_acknowledged &&
        importDetails.no_sensitive_or_prohibited_targets_acknowledged &&
        importDetails.unsubscribe_handling_acknowledged
    );
    const importDisabled = mutationDisabled || !isImportDetailsComplete;

    const buildEnglishPayload = useCallback(() => (
        isEnglishBeta ? { operator_surface: "english_beta", ...importDetails } : {}
    ), [isEnglishBeta, importDetails]);

    const fetchUnlockStatus = useCallback(async () => {
        if (!isEnglishBeta || !selectedClientId) return;
        setIsUnlockLoading(true);
        try {
            const params = new URLSearchParams({ client_id: selectedClientId, surface: "english_beta" });
            const res = await fetch(`${API_URL}/api/client/sales-lists/unlock-status?${params.toString()}`, {
                headers: getAuthHeader(),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || "Could not load unlock status.");
            setUnlockStatus(data);
        } catch (err) {
            setUnlockStatus({
                applies: true,
                unlocked: false,
                remaining_count: 1,
                checklist: [{
                    id: "unlock_status",
                    label: "Unlock status",
                    clear: false,
                    detail: err.message,
                }],
            });
        } finally {
            setIsUnlockLoading(false);
        }
    }, [isEnglishBeta, selectedClientId]);

    const fetchClientList = useCallback(async (page = 1, search = "") => {
        if (!selectedClientId) return;
        setIsLoading(true);
        try {
            const params = new URLSearchParams({ client_id: selectedClientId, page, limit: 100, searchTerm: search });
            const res = await fetch(`${API_URL}/api/client/sales-lists?${params.toString()}`, {
                headers: getAuthHeader(),
            });
            const data = await res.json();
            if (data.success) {
                setList(data.data || []);
                setPagination(data.pagination || null);
            } else {
                throw new Error(data.error || copy.empty);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [selectedClientId, copy.empty]);

    useEffect(() => {
        fetchClientList(currentPage, searchTerm);
    }, [currentPage, searchTerm, fetchClientList]);

    useEffect(() => {
        fetchUnlockStatus();
    }, [fetchUnlockStatus]);

    // ★追加: ページが変わったり検索した時に選択状態をリセット
    useEffect(() => {
        setSelectedIds([]);
    }, [list]);

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
        setUploadMessage("");
    };

    const handleFileUpload = async () => {
        if (isEnglishLocked) {
            setUploadMessage("Complete the unlock checklist before importing custom lists.");
            return;
        }
        if (!isImportDetailsComplete) {
            setUploadMessage(copy.attestation?.incomplete || copy.chooseFile);
            return;
        }
        if (!selectedFile) {
            setUploadMessage(copy.chooseFile);
            return;
        }
        setIsUploading(true);
        setUploadMessage(copy.uploadStarting);

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("client_id", selectedClientId);
        if (isEnglishBeta) {
            formData.append("operator_surface", "english_beta");
            Object.entries(importDetails).forEach(([key, value]) => {
                formData.append(key, typeof value === "boolean" ? String(value) : value);
            });
        }

        try {
            const res = await fetch(`${API_URL}/api/client/sales-lists/upload`, {
                method: "POST",
                headers: getAuthHeader(),
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            if (data.jobId) {
                setActiveJob({ id: data.jobId, status: 'starting', result_message: isEnglishBeta ? 'Custom list CSV import started.' : 'CSVアップロード処理を開始しました。' });
            }

            setUploadMessage(copy.uploadAccepted(data.jobId));
            setSelectedFile(null);
            document.getElementById('csv-upload-input').value = '';
        } catch (err) {
            setUploadMessage(`${copy.errorPrefix}${err.message}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (itemId) => {
        if (mutationDisabled) return;
        if (!window.confirm(copy.deleteConfirm)) return;
        try {
            const res = await fetch(`${API_URL}/api/client/sales-lists/${itemId}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({ client_id: selectedClientId, ...(isEnglishBeta ? { operator_surface: "english_beta" } : {}) })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            fetchClientList(currentPage, searchTerm);
        } catch (err) {
            alert(`${copy.deleteError}${err.message}`);
        }
    };

    // ★追加: 一括削除処理
    const handleBulkDelete = async () => {
        if (mutationDisabled) return;
        if (selectedIds.length === 0) return;
        if (!window.confirm(copy.bulkDeleteConfirm(selectedIds.length))) return;

        setIsDeleting(true);
        try {
            const res = await fetch(`${API_URL}/api/client/sales-lists/bulk-delete`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({ client_id: selectedClientId, ids: selectedIds, ...(isEnglishBeta ? { operator_surface: "english_beta" } : {}) })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            
            alert(data.message);
            setSelectedIds([]); // 選択をクリア
            fetchClientList(currentPage, searchTerm); // リストを再取得
        } catch (err) {
            alert(`${copy.bulkDeleteError}${err.message}`);
        } finally {
            setIsDeleting(false);
        }
    };

    // ★追加: 全選択/解除ハンドラー
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(list.map(item => item.id));
        } else {
            setSelectedIds([]);
        }
    };

    // ★追加: 個別選択/解除ハンドラー
    const handleSelectOne = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(itemId => itemId !== id));
        }
    };
    
    const handleSave = async (id, key, value) => {
        if (mutationDisabled) return;
        const originalItem = list.find(item => item.id === id);
        const updatedItem = { ...originalItem, [key]: value };

        if (!updatedItem.url) {
            alert(copy.urlRequired);
            return;
        }

        setList(prev => prev.map(item => item.id === id ? updatedItem : item));

        try {
            const payload = { ...updatedItem, client_id: selectedClientId };
            const res = await fetch(`${API_URL}/api/client/sales-lists/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({ ...payload, ...(isEnglishBeta ? { operator_surface: "english_beta" } : {}) })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
        } catch (err) {
            alert(`${copy.updateError}${err.message}`);
            setList(prev => prev.map(item => item.id === id ? originalItem : item));
        }
    };
    
    const handleAddNewRow = async () => {
        if (isEnglishLocked) {
            alert("Complete the unlock checklist before adding custom list records.");
            return;
        }
        if (!isImportDetailsComplete) {
            alert(copy.attestation?.incomplete || copy.chooseFile);
            return;
        }
        const companyName = prompt(copy.companyPrompt, "");
        if (!companyName) return;
        const companyUrl = prompt(copy.urlPrompt, "https://");
        if (!companyUrl || !companyUrl.startsWith('http')) {
            alert(copy.invalidUrl);
            return;
        }

        const newItem = { company: companyName, url: companyUrl };
        
        try {
            const res = await fetch(`${API_URL}/api/client/sales-lists/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...getAuthHeader(),
                },
                body: JSON.stringify({ client_id: selectedClientId, ...newItem, ...buildEnglishPayload() })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            alert(copy.addSuccess);
            fetchClientList(1, ""); // 最初のページを再読み込み
        } catch (err) {
            alert(`${copy.addError}${err.message}`);
        }
    };

    const handleDownloadCSV = async () => {
        try {
            // サーバーから全件データを取得
            const params = new URLSearchParams({ client_id: selectedClientId, download: 'true' });
            const res = await fetch(`${API_URL}/api/client/sales-lists?${params.toString()}`, {
                headers: getAuthHeader(),
            });
            const data = await res.json();
            
            if (!data.success) throw new Error(data.error || "データの取得に失敗しました。");
            if (!data.data || data.data.length === 0) {
                alert(copy.downloadNoData);
                return;
            }
            
            // CSVのヘッダーを定義
            const headers = isEnglishBeta
                ? [ "Company", "Company URL", "Contact URL", "Industry", "Postal Code", "Location", "Phone", "FAX", "Capital", "Employees", "Established", "Representative", "Business Tags", "List Tags" ]
                : [ "会社名", "会社URL", "問合せURL", "業種", "郵便番号", "住所", "電話番号", "FAX", "資本金", "従業員数", "設立日", "代表者名", "事業タグ", "リストタグ" ];
            
            // サーバーからのデータをCSVの形式にマッピング
            const dataForCsv = data.data.map(item => ({
                [headers[0]]: item.company,
                [headers[1]]: item.url,
                [headers[2]]: item.contact_url,
                [headers[3]]: item.industry_class,
                [headers[4]]: item.postal_code,
                [headers[5]]: item.address,
                [headers[6]]: item.phone,
                'FAX': item.fax,
                [headers[8]]: item.capital,
                [headers[9]]: item.employee_count,
                [headers[10]]: item.establishment_date,
                [headers[11]]: item.representative_name,
                [headers[12]]: item.business_tags,
                [headers[13]]: item.list_tags
            }));

            // Build-time pinned PapaParse avoids runtime third-party scripts.
            const csv = Papa.unparse({ fields: headers, data: dataForCsv });
            
            // BOM（バイトオーダーマーク）を追加して文字化けを防ぐ
            const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            
            // ダウンロード用のリンクを作成してクリック
            const a = document.createElement("a");
            a.href = url;
            a.download = isEnglishBeta ? "custom_list.csv" : "my_sales_list.csv";
            document.body.appendChild(a);
            a.click();
            
            // 後処理
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(`${copy.downloadError}${err.message}`);
        }
    };

    const handleDownloadTemplate = () => {
        fetch(`${API_URL}/api/client/sales-lists/template`, {
            headers: getAuthHeader(),
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(copy.templateDownloadError);
            }
            return response.blob();
        })
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = isEnglishBeta ? "custom_list_template.csv" : "list_template.csv";
            document.body.appendChild(a);
            a.click();
            a.remove(); 
            window.URL.revokeObjectURL(url);
        })
        .catch(err => {
            console.error("雛形CSVのダウンロードエラー:", err);
            alert(err.message);
        });
    };

    const updateImportDetail = (key, value) => {
        setImportDetails(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-screen-2xl mx-auto">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">{copy.title}</h1>

                {isEnglishBeta && (
                    <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-md sm:p-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-600">{copy.unlock.title}</p>
                                <h2 className="mt-2 text-xl font-black text-slate-900">
                                    {unlockStatus?.unlocked ? copy.unlock.unlockedTitle : copy.unlock.lockedTitle}
                                </h2>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                    {isUnlockLoading
                                        ? copy.unlock.loading
                                        : unlockStatus?.unlocked
                                            ? copy.unlock.unlockedBody
                                            : copy.unlock.lockedBody(unlockStatus?.remaining_count || 0)}
                                </p>
                            </div>
                            <a
                                href={trackingPath}
                                className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
                            >
                                {copy.unlock.setupLink}
                            </a>
                        </div>
                        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {(unlockStatus?.checklist || []).map((item) => (
                                <div key={item.id} className={`rounded-lg border p-4 ${item.clear ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <p className="font-bold text-slate-900">{item.label}</p>
                                        <span className={`rounded-full px-2 py-1 text-[11px] font-black uppercase tracking-wider ${item.clear ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
                                            {item.clear ? copy.unlock.clear : copy.unlock.required}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-xs leading-5 text-slate-600">{item.detail}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isEnglishBeta && (
                    <div className={`mb-6 rounded-lg border bg-white p-4 shadow-md sm:p-6 ${isEnglishLocked ? 'border-slate-200 opacity-75' : 'border-indigo-100'}`}>
                        <div className="mb-4">
                            <h2 className="text-lg font-black text-slate-900">{copy.attestation.title}</h2>
                            <p className="mt-1 text-sm text-slate-600">{copy.attestation.body}</p>
                        </div>
                        <div className="grid gap-4 lg:grid-cols-2">
                            <label className="block">
                                <span className="text-sm font-bold text-slate-700">{copy.attestation.targetCountry}</span>
                                <input
                                    type="text"
                                    value={importDetails.target_country}
                                    onChange={(e) => updateImportDetail('target_country', e.target.value)}
                                    disabled={isEnglishLocked}
                                    placeholder={copy.attestation.targetCountryPlaceholder}
                                    className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm disabled:bg-slate-100"
                                />
                            </label>
                            <label className="block">
                                <span className="text-sm font-bold text-slate-700">{copy.attestation.listSource}</span>
                                <select
                                    value={importDetails.list_source}
                                    onChange={(e) => updateImportDetail('list_source', e.target.value)}
                                    disabled={isEnglishLocked}
                                    className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm disabled:bg-slate-100"
                                >
                                    {copy.attestation.sourceOptions.map(([value, label]) => (
                                        <option key={value || 'empty'} value={value}>{label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-sm font-bold text-slate-700">{copy.attestation.sourceDetail}</span>
                                <textarea
                                    value={importDetails.source_url_or_description}
                                    onChange={(e) => updateImportDetail('source_url_or_description', e.target.value)}
                                    disabled={isEnglishLocked}
                                    placeholder={copy.attestation.sourceDetailPlaceholder}
                                    rows={3}
                                    className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm disabled:bg-slate-100"
                                />
                            </label>
                            <label className="block">
                                <span className="text-sm font-bold text-slate-700">{copy.attestation.intendedUse}</span>
                                <textarea
                                    value={importDetails.intended_use}
                                    onChange={(e) => updateImportDetail('intended_use', e.target.value)}
                                    disabled={isEnglishLocked}
                                    placeholder={copy.attestation.intendedUsePlaceholder}
                                    rows={3}
                                    className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm disabled:bg-slate-100"
                                />
                            </label>
                        </div>
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                            {[
                                ['has_permission_or_legitimate_basis', copy.attestation.permission],
                                ['sender_responsibility_acknowledged', copy.attestation.responsibility],
                                ['no_sensitive_or_prohibited_targets_acknowledged', copy.attestation.noSensitive],
                                ['unsubscribe_handling_acknowledged', copy.attestation.unsubscribe],
                            ].map(([key, label]) => (
                                <label key={key} className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(importDetails[key])}
                                        onChange={(e) => updateImportDetail(key, e.target.checked)}
                                        disabled={isEnglishLocked}
                                        className="mt-1 h-4 w-4"
                                    />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
                    <h2 className="text-lg sm:text-xl font-semibold mb-2">{copy.operationsTitle}</h2>
                    {copy.uploadEncodingNote && <p className="mb-4 text-sm text-gray-600">{copy.uploadEncodingNote}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-semibold text-gray-700 mb-2">{copy.uploadTitle}</h3>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                                <input id="csv-upload-input" type="file" accept=".csv" onChange={handleFileChange} disabled={mutationDisabled} className="w-full flex-grow text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"/>
                                <button onClick={handleFileUpload} disabled={isUploading || importDisabled} className="w-full rounded-md bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400 sm:w-auto">
                                    {isUploading ? copy.uploading : copy.execute}
                                </button>
                            </div>
                            {uploadMessage && <p className="mt-2 text-sm text-gray-600">{uploadMessage}</p>}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-700 mb-2">{copy.otherTitle}</h3>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <button onClick={handleAddNewRow} disabled={importDisabled} className="flex items-center justify-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400"><PlusIcon /> {copy.addManual}</button>
                                <button onClick={handleDownloadCSV} className="flex items-center justify-center gap-2 bg-gray-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-gray-700"><DownloadIcon /> {copy.downloadAll}</button>
                                <button onClick={handleDownloadTemplate} className="flex items-center justify-center gap-2 bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-md hover:bg-gray-300">
                                    <DownloadIcon /> {copy.downloadTemplate}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md">
                    <div className="p-4 border-b flex flex-col justify-between gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={copy.searchPlaceholder} className="w-full p-2 border rounded-md md:w-1/3"/>
                        
                        {/* ★追加: 選択中のみ表示される一括削除ボタン */}
                        {selectedIds.length > 0 && (
                            <button 
                                onClick={handleBulkDelete} 
                                disabled={isDeleting || mutationDisabled}
                                className="flex w-full items-center justify-center gap-2 bg-red-100 text-red-600 font-bold py-2 px-4 rounded-md hover:bg-red-200 border border-red-200 transition-colors sm:w-auto"
                            >
                                <TrashIcon />
                                {isDeleting ? copy.deleting : copy.bulkDelete(selectedIds.length)}
                            </button>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <div className="divide-y divide-gray-100 md:hidden">
                            {isLoading ? (
                                <div className="p-8 text-center text-sm text-gray-500">{copy.loading}</div>
                            ) : error ? (
                                <div className="p-8 text-center text-sm text-red-500">{copy.errorPrefix}{error}</div>
                            ) : list.length > 0 ? list.map(item => (
                                <div key={`${item.id}-mobile`} className={`p-4 ${selectedIds.includes(item.id) ? 'bg-blue-50/60' : ''}`}>
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer"
                                            checked={selectedIds.includes(item.id)}
                                            disabled={mutationDisabled}
                                            onChange={(e) => handleSelectOne(e, item.id)}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-gray-900 break-words">{item.company || copy.dash}</p>
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs text-blue-600 hover:underline break-all">{item.url || copy.noUrl}</a>
                                        </div>
                                        <button onClick={() => handleDelete(item.id)} disabled={mutationDisabled} className="flex-shrink-0 rounded-md bg-red-50 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-40">{copy.columns.delete}</button>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-600">
                                        <p><span className="font-semibold text-gray-800">{copy.columns.contactUrl}:</span> <span className="break-all">{item.contact_url || copy.dash}</span></p>
                                        <p><span className="font-semibold text-gray-800">{copy.columns.industry}:</span> {item.industry_class || copy.dash}</p>
                                        <p><span className="font-semibold text-gray-800">{copy.columns.address}:</span> {item.address || copy.dash}</p>
                                        <p><span className="font-semibold text-gray-800">{copy.columns.phone}:</span> {item.phone || copy.dash}</p>
                                    </div>
                                    {(item.business_tags || item.list_tags) && (
                                        <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                            {item.business_tags && <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700">{item.business_tags}</span>}
                                            {item.list_tags && <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">{item.list_tags}</span>}
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <div className="p-8 text-center text-sm text-gray-500">{copy.empty}</div>
                            )}
                        </div>
                        <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                            <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                                <tr>
                                    {/* ★追加: ヘッダーの全選択チェックボックス */}
                                    <th className="py-3 px-3 text-center w-10">
                                        <input 
                                            type="checkbox" 
                                            className="w-4 h-4 cursor-pointer"
                                            onChange={handleSelectAll} 
                                            disabled={mutationDisabled}
                                            checked={list.length > 0 && selectedIds.length === list.length} 
                                        />
                                    </th>
                                    <th className="py-3 px-3">{copy.columns.company}</th>
                                    <th className="py-3 px-3">{copy.columns.url}</th>
                                    <th className="py-3 px-3">{copy.columns.contactUrl}</th>
                                    <th className="py-3 px-3">{copy.columns.industry}</th>
                                    <th className="py-3 px-3">{copy.columns.postalCode}</th>
                                    <th className="py-3 px-3">{copy.columns.address}</th>
                                    <th className="py-3 px-3">{copy.columns.phone}</th>
                                    <th className="py-3 px-3">{copy.columns.capital}</th>
                                    <th className="py-3 px-3">{copy.columns.employees}</th>
                                    <th className="py-3 px-3">{copy.columns.established}</th>
                                    <th className="py-3 px-3">{copy.columns.representative}</th>
                                    <th className="py-3 px-3">{copy.columns.businessTags}</th>
                                    <th className="py-3 px-3">{copy.columns.listTags}</th>
                                    <th className="py-3 px-3">{copy.columns.actions}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr><td colSpan="15" className="text-center p-8">{copy.loading}</td></tr>
                                ) : error ? (
                                    <tr><td colSpan="15" className="text-center p-8 text-red-500">{copy.errorPrefix}{error}</td></tr>
                                ) : list.map(item => (
                                    <tr key={item.id} className={`hover:bg-gray-50 ${selectedIds.includes(item.id) ? 'bg-blue-50/50' : ''}`}>
                                        {/* ★追加: 各行のチェックボックス */}
                                        <td className="py-2 px-3 border-b border-gray-200 text-center">
                                            <input 
                                                type="checkbox" 
                                                className="w-4 h-4 cursor-pointer"
                                                checked={selectedIds.includes(item.id)} 
                                                disabled={mutationDisabled}
                                                onChange={(e) => handleSelectOne(e, item.id)} 
                                            />
                                        </td>
                                        <EditableCell value={item.company} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'company', v)} />
                                        <EditableCell value={item.url} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'url', v)} />
                                        <EditableCell value={item.contact_url} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'contact_url', v)} />
                                        <EditableCell value={item.industry_class} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'industry_class', v)} />
                                        <EditableCell value={item.postal_code} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'postal_code', v)} />
                                        <EditableCell value={item.address} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'address', v)} />
                                        <EditableCell value={item.phone} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'phone', v)} />
                                        <EditableCell value={item.capital} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'capital', v)} />
                                        <EditableCell value={item.employee_count} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'employee_count', v)} />
                                        <EditableCell value={item.establishment_date} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'establishment_date', v)} />
                                        <EditableCell value={item.representative_name} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'representative_name', v)} />
                                        <EditableCell value={item.business_tags} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'business_tags', v)} />
                                        <EditableCell value={item.list_tags} emptyText={copy.dash} disabled={mutationDisabled} onSave={(v) => handleSave(item.id, 'list_tags', v)} />
                                        <td className="py-2 px-3 border-b border-gray-200 whitespace-nowrap">
                                            <button onClick={() => handleDelete(item.id)} disabled={mutationDisabled} className="text-red-600 hover:text-red-800 text-xs font-bold px-2 py-1 bg-red-50 rounded hover:bg-red-100 transition-colors disabled:opacity-40">{copy.columns.delete}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 border-t flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-sm text-gray-600">{pagination ? copy.pageTotal(pagination.totalItems) : ''}</span>
                        <PaginationWithCopy currentPage={currentPage} totalPages={pagination ? pagination.totalPages : 0} onPageChange={setCurrentPage} copy={copy} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ClientSalesListPage;
